import { useI18nSubscription } from '../../../i18n/useI18nSubscription.js';
import { i18n } from '../../../../i18n/index.js';
import { useState, type ReactElement } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { Button, Card, CardContent, CardHeader, CardTitle } from '@govard/ui-primitives';

import { getEntityActivitiesQueryKey } from '../../../activity/api/activities.client.js';
import {
  decideApproval,
  type ApprovalExecutionOutcome,
  type ApprovalStatus,
} from '../../../approvals/api/approvals.client.js';
import { addDealNote } from '../../../deals/api/deals.client.js';
import { getNotesQueryKey } from '../../../notes/api/notes.client.js';
import { useCurrentScope, useScopeStore } from '../../../../shared/scope/useCurrentScope.js';

import type { StructuredMessagePayload } from '@govard/ai-chat-kit';

interface AddDealNoteConfirmationSnippetProps {
  note: Extract<StructuredMessagePayload, { type: 'add_deal_note_confirmation' }>['note'];
  action: Extract<StructuredMessagePayload, { type: 'add_deal_note_confirmation' }>['action'];
  approvalStatus?: ApprovalStatus | undefined;
  executionOutcome?: ApprovalExecutionOutcome | undefined;
}

type ConfirmationStatus = 'idle' | 'saving' | 'saved' | 'error' | 'verification';

function initialConfirmationStatus(
  hasApproval: boolean,
  approvalStatus: ApprovalStatus | undefined,
  executionOutcome: ApprovalExecutionOutcome | undefined,
): ConfirmationStatus {
  if (!hasApproval) return 'idle';
  if (executionOutcome?.status === 'succeeded') return 'saved';
  if (
    executionOutcome?.status === 'failed' ||
    executionOutcome?.status === 'rejected' ||
    executionOutcome?.status === 'expired' ||
    executionOutcome?.status === 'cancelled'
  ) {
    return 'error';
  }
  if (approvalStatus === 'approved') return 'verification';
  if (
    approvalStatus === 'rejected' ||
    approvalStatus === 'expired' ||
    approvalStatus === 'cancelled'
  ) {
    return 'error';
  }
  return 'idle';
}

export function AddDealNoteConfirmationSnippet({
  note,
  action,
  approvalStatus,
  executionOutcome,
}: AddDealNoteConfirmationSnippetProps): ReactElement {
  useI18nSubscription();
  const queryClient = useQueryClient();
  const scope = useCurrentScope();
  const workspaceId = scope?.workspaceId ?? 'anonymous';
  const [status, setStatus] = useState<ConfirmationStatus>(() =>
    initialConfirmationStatus(
      action.payload.approvalId !== undefined,
      approvalStatus,
      executionOutcome,
    ),
  );

  const handleConfirm = async (): Promise<void> => {
    setStatus('saving');

    try {
      if (action.payload.approvalId === undefined) {
        await addDealNote(action.payload.dealId, { body: action.payload.body });
      } else {
        const approval = await decideApproval({
          approvalId: action.payload.approvalId,
          decision: 'approve',
        });
        if (approval.executionOutcome?.status === 'succeeded') {
          // Continue to the common saved/cache-refresh path below.
        } else if (approval.executionOutcome?.status === 'failed') {
          setStatus('error');
          return;
        } else {
          setStatus('verification');
          return;
        }
      }
    } catch {
      setStatus('error');
      return;
    }

    setStatus('saved');

    const scopeState = useScopeStore.getState();
    if (scopeState.isSwitchingWorkspace || scopeState.scope?.workspaceId !== workspaceId) {
      return;
    }

    void Promise.allSettled([
      queryClient.invalidateQueries({ queryKey: ['deals', workspaceId] }),
      queryClient.invalidateQueries({ queryKey: ['approvals', workspaceId] }),
      queryClient.invalidateQueries({
        queryKey: getNotesQueryKey(workspaceId).slice(0, 2),
      }),
      queryClient.invalidateQueries({
        queryKey: getEntityActivitiesQueryKey(workspaceId, 'deal', action.payload.dealId),
      }),
    ]);
  };

  return (
    <Card data-testid="structured-snippet-add-deal-note-confirmation">
      <CardHeader>
        <CardTitle>
          {i18n.t(
            'crm.features.features.ai.chat.components.snippets.adddealnoteconfirmationsnippet.dobavit_zametku',
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-stone-500">
            {i18n.t(
              'crm.features.features.ai.chat.components.snippets.adddealnoteconfirmationsnippet.sdelka',
            )}
          </p>
          <p className="font-medium text-stone-900">{note.dealTitle}</p>
        </div>
        <p className="whitespace-pre-wrap rounded-lg border border-amber-100 bg-amber-50 px-3 py-2 text-stone-800">
          {note.body}
        </p>
        <Button
          type="button"
          size="sm"
          disabled={status !== 'idle'}
          onClick={() => {
            void handleConfirm();
          }}
        >
          {status === 'saved'
            ? i18n.t(
                'crm.features.features.ai.chat.components.snippets.adddealnoteconfirmationsnippet.zametka_dobavlena',
              )
            : status === 'verification'
              ? i18n.t(
                  'crm.features.features.ai.chat.components.snippets.adddealnoteconfirmationsnippet.rezultat_proveryaetsya',
                )
              : i18n.t('crm.ai_ui.add_note.confirm')}
        </Button>
        {status === 'error' ? (
          <p className="text-xs text-red-600">
            {i18n.t(
              'crm.features.features.ai.chat.components.snippets.adddealnoteconfirmationsnippet.ne_udalos_dobavit_zametku_poprobuyte_eshche',
            )}
          </p>
        ) : null}
        {status === 'verification' ? (
          <p className="text-xs text-stone-600">
            {i18n.t(
              'crm.features.features.ai.chat.components.snippets.adddealnoteconfirmationsnippet.podtverzhdenie_sokhraneno_rezultat_proveryaetsya',
            )}
          </p>
        ) : null}
      </CardContent>
    </Card>
  );
}
